import * as React from "react";
import { LayerHost, Stack } from "@fluentui/react";
import { BookmarkStyle } from "../../constants/uiConstants";
import { AppContext, AppState } from "../Context/AppContext";
import { Breadcrumbs } from "../Navigation/Breadcrumbs";
import { regsiterBreadcrumbLabel, useNavigation } from "../Navigation/Navigation";

export const PageAnchorId = "page";

export interface BookmarkablePageProps<T extends AppState> {
  appContext: AppContext<T>;
  /**
   * Registration ids for the BookmarkablePage
   */
  registrationIds: string[];
  /**
   * Title for the BookmarkablePage
   */
  title: string;
  /**
   * Title suffix for the BookmarkablePage
   */
  titleSuffix?: string;
  /**
   * Components to be rendered as part of the BookmarkablePage
   */
  children: React.ReactNode;
}

/**
 * The BookmarkablePage component renders the components specified by the caller along
 * with the breadcrumbs
 */
// eslint-disable-next-line react/function-component-definition
export function BookmarkablePage<T extends AppState>(
  { appContext, registrationIds, title, titleSuffix, children }: BookmarkablePageProps<T>,
): JSX.Element {
  const { setPageDetails } = useNavigation(appContext);
  React.useEffect(() => {
    document.title = titleSuffix ? `${title} - ${titleSuffix}` : title;
    setPageDetails(title, titleSuffix || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, titleSuffix]);

  registrationIds.forEach(id => {
    regsiterBreadcrumbLabel(id, title);
  });

  const appState = React.useContext<T>(appContext);

  const heightDeduction = Number(appState.headerHeight)
    + Number(appState.footerHeight ? appState.footerHeight : 0)
    + BookmarkStyle.height
    + BookmarkStyle.paddingTop;

  const layerHostHeight = `calc(100vh - ${heightDeduction}px)`;
  return (
    <Stack>
      <Breadcrumbs />
      <LayerHost id={PageAnchorId} style={{ position: "relative", width: "100%", height: layerHostHeight }}>
        {children}
      </LayerHost>
    </Stack>
  );
}
